//   src/services/hijriService.js
import axios from "axios";
import HijriDate from "hijri-date/lib/safe";

// Lightweight cache (memory + localStorage)
const mem = new Map();
const isBrowser = typeof window !== "undefined";

const HIJRI_API_URL = "https://api.aladhan.com/v1/gToH?date=";
const HIJRI_TTL = 24 * 60 * 60 * 1000; // 24h
const HIJRI_STALE = 2 * 60 * 60 * 1000; // refresh after 2h in background

const now = () => Date.now();
const keyFor = (dateIso, tz) => `hijri:${tz}:${dateIso}`; // dateIso = YYYY-MM-DD in tz

function readLS(key) {
  if (!isBrowser) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj && typeof obj === "object" ? obj : null;
  } catch {
    return null;
  }
}
function writeLS(key, value) {
  if (!isBrowser) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function getCached(key) {
  const m = mem.get(key);
  if (m && m.expiresAt > now()) return m.value;
  const ls = readLS(key);
  if (ls && ls.expiresAt > now()) {
    mem.set(key, ls);
    return ls.value;
  }
  return null;
}
function setCached(key, value, ttlMs) {
  const entry = { value, cachedAt: now(), expiresAt: now() + ttlMs };
  mem.set(key, entry);
  writeLS(key, entry);
}
function isStale(key, staleMs) {
  const entry = mem.get(key) || readLS(key);
  if (!entry) return true;
  return now() - entry.cachedAt > staleMs;
}
function peekCached(key) {
  const m = mem.get(key);
  if (m) return m.value ?? null;
  const ls = readLS(key);
  return ls ? ls.value ?? null : null;
}

// --- API + fallback ---
async function fetchHijriFromApi_DDMMYYYY(ddmmyyyy, lat = null, long = null) {
  let url = `${HIJRI_API_URL}${ddmmyyyy}`;
  if (lat != null && long != null) url += `&latitude=${lat}&longitude=${long}`;

  const res = await axios.get(url);
  const hijri = res.data?.data?.hijri;

  if (hijri && hijri.month?.en && hijri.day && hijri.year) {
    return {
      hijri: `${hijri.month.en.toUpperCase()} ${hijri.day} ${hijri.year}`,
      iso: null,
    };
  }
  throw new Error("Invalid Hijri data from API");
}

function computeHijriLocalFromISO(iso) {
  // iso = "YYYY-MM-DD"
  try {
    const [Y, M, D] = iso.split("-").map((x) => parseInt(x, 10));
    const gDate = new Date(Y, M - 1, D);
    const h = new HijriDate(gDate);
    return {
      hijri: `${h.getMonthName().toUpperCase()} ${h.getDate()} ${h.getFullYear()}`,
      iso,
    };
  } catch {
    return { hijri: "UNKNOWN HIJRI DATE", iso };
  }
}

// --- Public API ---
// Instant sync hydration (may be stale) to avoid UI delay
export function peekHijriDate(dateIso, tz) {
  const key = keyFor(dateIso, tz);
  return (
    peekCached(key) ||
    computeHijriLocalFromISO(dateIso) // instant local fallback
  );
}

// Cached fetch with background refresh
export async function getHijriDateCached(dateIso, tz, { background = true, lat = null, long = null } = {}) {
  const key = keyFor(dateIso, tz);
  const cached = getCached(key);
  const stale = isStale(key, HIJRI_STALE);

  if (background && stale) {
    // fire-and-forget normalize via API (optional)
    (async () => {
      try {
        // API expects DD-MM-YYYY
        const [Y, M, D] = dateIso.split("-");
        const ddmmyyyy = `${D}-${M}-${Y}`;
        const apiVal = await fetchHijriFromApi_DDMMYYYY(ddmmyyyy, lat, long);
        setCached(key, apiVal, HIJRI_TTL);
      } catch {
        // fall back to local compute
        const local = computeHijriLocalFromISO(dateIso);
        setCached(key, local, HIJRI_TTL);
      }
    })();
  }

  if (cached) return { data: cached, fromCache: true };

  // Cold path: compute locally now, then (optionally) improve via API
  const local = computeHijriLocalFromISO(dateIso);
  setCached(key, local, HIJRI_TTL);

  try {
    const [Y, M, D] = dateIso.split("-");
    const ddmmyyyy = `${D}-${M}-${Y}`;
    const apiVal = await fetchHijriFromApi_DDMMYYYY(ddmmyyyy, lat, long);
    setCached(key, apiVal, HIJRI_TTL);
    return { data: apiVal, fromCache: false };
  } catch {
    return { data: local, fromCache: true };
  }
}

// Back-compat: if other code still imports fetchHijriDate(dd-mm-yyyy, lat, long)
export async function fetchHijriDate(ddmmyyyy, lat = null, long = null) {
  try {
    const apiVal = await fetchHijriFromApi_DDMMYYYY(ddmmyyyy, lat, long);
    return apiVal.hijri;
  } catch (err) {
    // derive ISO to compute locally
    try {
      const [D, M, Y] = ddmmyyyy.split("-");
      const iso = `${Y}-${M}-${D}`;
      return computeHijriLocalFromISO(iso).hijri;
    } catch {
      return "UNKNOWN HIJRI DATE";
    }
  }
}
